import React, { useMemo, useState } from 'react';
import { FiClock, FiRotateCcw, FiX } from 'react-icons/fi';
import './VersionHistoryPanel.css';

const htmlToPlain = (value = '') =>
  String(value)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();

const formatWhen = (iso) => {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const previewOf = (body) => {
  const text = htmlToPlain(body);
  if (!text) {
    return 'Empty draft';
  }
  return text.length > 140 ? `${text.slice(0, 137)}…` : text;
};

const VersionHistoryPanel = ({
  versions = [],
  loading = false,
  currentTitle = '',
  currentBody = '',
  canRestore = true,
  saving = false,
  onRestore,
  onClose,
}) => {
  const [selectedId, setSelectedId] = useState(versions[0]?.id || 'current');
  const rows = useMemo(
    () => [
      {
        id: 'current',
        title: currentTitle || 'Untitled draft',
        body: currentBody,
        label: 'Current draft',
        meta: 'In the editor now',
      },
      ...versions.map((version) => ({
        id: version.id,
        title: version.title || 'Untitled draft',
        body: version.body,
        label: formatWhen(version.created_at) || 'Saved version',
        meta: version.created_by_name || 'Saved draft',
      })),
    ],
    [currentBody, currentTitle, versions]
  );
  const selected = rows.find((row) => String(row.id) === String(selectedId)) || rows[0];

  return (
    <section className="vh" aria-label="Version history">
      <header className="vh-head">
        <div>
          <h3>Previous saves of this document</h3>
          <p>Restore an earlier copy of the text. Comments stay on the current draft.</p>
        </div>
        {onClose ? (
          <button type="button" className="vh-close" onClick={onClose} aria-label="Close version history">
            <FiX size={18} />
          </button>
        ) : null}
      </header>

      {loading ? <p className="vh-status">Loading versions…</p> : null}

      {!loading && !versions.length ? (
        <p className="vh-empty">
          <FiClock size={16} />
          No earlier copy yet. Edit the document, then save.
        </p>
      ) : null}

      <ul className="vh-list">
        {rows.map((row) => {
          const active = String(selected?.id) === String(row.id);
          return (
            <li key={row.id}>
              <button
                type="button"
                className={active ? 'is-active' : ''}
                onClick={() => setSelectedId(row.id)}
              >
                <span className="vh-list__label">{row.label}</span>
                <span className="vh-list__meta">{row.meta}</span>
                <span className="vh-list__preview">{previewOf(row.body)}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {selected && selected.id !== 'current' && canRestore && onRestore ? (
        <div className="vh-actions">
          <button
            type="button"
            className="vh-restore"
            disabled={saving}
            onClick={() => onRestore(selected.id)}
          >
            <FiRotateCcw size={14} /> Restore this version
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default VersionHistoryPanel;
